import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import UKFlag from '@/assets/icons/UKFlag'
import NLFlag from '@/assets/icons/NLFlag'
import { FilterDropdown } from './FilterDropdown'
import { useMutationGraphQL } from '@/use-graphql'
import { updateLanguageMutation } from './navbar/graphql/mutation'
import useAuthStore from '@/stores/useAuthStore'
import { useQueryClient } from '@tanstack/react-query'

const languageOptions = [
  { code: 'en', flag: <UKFlag className="w-6" /> },
  { code: 'nl', flag: <NLFlag className="w-6" /> },
]

const LanguageSelect = () => {
  const queryClient = useQueryClient()
  const { i18n } = useTranslation()
  const { user } = useAuthStore()

  const [value, setValue] = useState<string>(
    user?.language || localStorage.getItem('language') || 'nl',
  )
  const userInitiated = useRef(false)

  const updateLanguage = useMutationGraphQL(
    updateLanguageMutation,
    // @ts-ignore
    { language: value },
    { onSuccess: () => setTimeout(() => queryClient.invalidateQueries({ type: 'active' }), 500) },
  )

  // Sync language to i18n/localStorage whenever value changes, and call mutation only on user selection
  useEffect(() => {
    localStorage.setItem('language', value)
    if (i18n.language !== value) {
      i18n.changeLanguage(value)
    }
    if (userInitiated.current && user) {
      updateLanguage.mutate()
      userInitiated.current = false
    }
  }, [value])

  // Sync to user's language from authstore when user loads (without triggering mutation)
  useEffect(() => {
    if (user?.language && user.language !== value) {
      setValue(user.language)
    }
  }, [user?.language])

  const handleChange = (newValue: string | undefined) => {
    if (!newValue) return
    userInitiated.current = true
    setValue(newValue)
  }

  return (
    <FilterDropdown
      popupClassName="sm:-ml-4 sm:mt-3"
      contentClassName="sm:max-h-[25vh]"
      showSearch={false}
      contentWidth={150}
      value={value}
      onChange={handleChange}
      options={languageOptions.map((e) => ({
        value: e.code,
        label: (
          <div className="flex items-center gap-4">
            {e.flag}
            <span>{e.code.toUpperCase()}</span>
          </div>
        ),
      }))}
      customButton={languageOptions.find((e) => e.code === value)?.flag}
      hideCancel
      hideOnClick
      sideOffset={-10}
    />
  )
}

export default LanguageSelect
